/**
 * Scope document CSS under a wrapper selector so it can render inline in the
 * app DOM without element selectors (h1, p, body…) leaking into the chrome.
 * `:root`, `html` and `body` selectors become the scope itself; everything
 * else is prefixed. `@media` blocks recurse; other at-rules (`@page`,
 * `@font-face`, `@keyframes`) pass through untouched.
 */
export function scopeCss(css: string, scope: string): string {
  let out = '';
  let i = 0;

  const scopeSelector = (selectorList: string): string => {
    const scoped = selectorList
      .split(',')
      .map((raw) => {
        const s = raw.trim();
        if (s === '') return s;
        // :root / html / body collapse onto the scope root
        const rootish = s
          .replace(/^:root\b/, scope)
          .replace(/^html\b/, scope)
          .replace(/^body\b/, scope);
        if (rootish !== s) return rootish;
        if (s === '*') return `${scope} *`;
        return `${scope} ${s}`;
      })
      .filter((s) => s !== '');
    return [...new Set(scoped)].join(',');
  };

  const readBlock = (from: number): number => {
    // returns index just past the matching closing brace (from points at '{')
    let depth = 0;
    for (let j = from; j < css.length; j++) {
      if (css[j] === '{') depth++;
      else if (css[j] === '}') {
        depth--;
        if (depth === 0) return j + 1;
      }
    }
    return css.length;
  };

  while (i < css.length) {
    const brace = css.indexOf('{', i);
    if (brace === -1) {
      out += css.slice(i);
      break;
    }
    const selector = css.slice(i, brace);
    const trimmed = selector.trim();
    if (trimmed.startsWith('@media')) {
      const end = readBlock(brace);
      const inner = css.slice(brace + 1, end - 1);
      out += `${selector}{${scopeCss(inner, scope)}}`;
      i = end;
    } else if (trimmed.startsWith('@')) {
      const end = readBlock(brace);
      out += css.slice(i, end);
      i = end;
    } else {
      const close = css.indexOf('}', brace);
      const end = close === -1 ? css.length : close + 1;
      const leading = selector.match(/^\s*/)?.[0] ?? '';
      out += `${leading}${scopeSelector(selector)}${css.slice(brace, end)}`;
      i = end;
    }
  }
  return out;
}
